import {$qsAll} from '../../../../../@shared/ts/dom';
import {type StripeGateway, type IStripeContext} from '../../../models/stripe';
import {Feature} from '../../../reducers/environmentReducer';
import {PeachPayCustomer} from '../../../reducers/peachPayCustomerReducer';
import {getLocaleText} from '../../../util/translation';
import idealBadgeURL from '../../../../img/badge/ideal.svg';
import idealImageURL from '../../../../img/marks/ideal.svg';
import {FeatureFlag} from '../../../../../@type/features';

function initStripeIdealMethod(): StripeGateway {
	return {
		config: {
			name: Feature.metadata<string>(FeatureFlag.STRIPE_IDEAL_GATEWAY, 'title') ?? 'iDEAL',
			gatewayId: 'peachpay_stripe_ideal',
			description: getLocaleText('After selecting <b>Pay</b> you will be redirected to complete your payment.'),
			assets: {
				title: {src: idealImageURL},
				badge: {src: idealBadgeURL},
			},
		},
		init(context: IStripeContext) {
			const {elements} = context;
			const options = {
				style: {
					base: {
						padding: '5px 0',
						color: '#32325d',
						fontSize: '16px',
					},
				},
			};

			const $idealBank = elements.create('idealBank', options);
			$qsAll('div.pp-pm-saved-option[data-gateway="peachpay_stripe_ideal"]', $el => {
				$el.innerHTML = /* html */ `
<div style="text-align: left; display: flex; flex-direction: column;">
    <label>
        <span style="font-weight: 600;">${getLocaleText('Account name')}</span>
    </label>
    <span class="ideal-accountholder-name" style="margin-bottom: 1rem;">${PeachPayCustomer.billing.fullName()}</span>
    <div style="border-radius: 4px; background-color: #f4f4f4; padding: 0.1rem 0.5rem;">
        <label style="font-weight: 600; font-size: 13px; color: #707070;">${getLocaleText('iDEAL Bank')}</label>
        <div class="ideal-container-element"></div>
    </div>
    <hr>
    <p class="muted" style="text-align: center; margin: 0.5rem 0">${getLocaleText('After selecting <b>Pay</b> you will be redirected to complete your payment.')}</p>
</div>
`;
			});

			// Always update customer full name
			$qsAll('.ideal-accountholder-name', $el => {
				$el.innerHTML = PeachPayCustomer.billing.fullName();
			});

			$idealBank.mount('#pp-pms-new div.pp-pm-saved-option[data-gateway="peachpay_stripe_ideal"] .ideal-container-element');
		},
		async tokenize(context) {
			const {stripe, elements} = context;

			const $idealBank = elements.getElement('idealBank');
			if (!$idealBank) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to find the required Stripe element.',
				}];
			}

			const {paymentMethod, error} = await stripe.createPaymentMethod({
				type: 'ideal',
				ideal: $idealBank,
				billing_details: PeachPayCustomer.stripeBillingDetails(),
			});

			if (error) {
				const message = error.message ?? 'Failed to tokenize payment method.';

				return [null, {
					paymentStatus: 'creationfailed',
					note: message,
				}];
			}

			if (!paymentMethod?.id) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to tokenize payment method.',
				}];
			}

			return [paymentMethod.id, {}];
		},
		async confirm(context: IStripeContext, clientSecret: string, intermediateURL: string) {
			const {stripe} = context;

			const {paymentIntent, error} = await stripe.confirmIdealPayment(clientSecret, {return_url: intermediateURL});

			if (error) {
				const message = error.message ?? 'Failed to confirm payment.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent) {
				return ['success', {}];
			}

			return [null, {}];
		},
	};
}

export {initStripeIdealMethod};
